import * as React from 'react'
import { View, FlatList, Text, TouchableOpacity } from 'react-native'
import styled from '@theme/styled-components'

import Root from '@components/root/Root'
import Header from '@components/header/Header'
import TextInputComponent from '@components/textInput/TextInput'
import RoleCard from '@components/card/RoleCard'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useBreakpoint from '@utils/useBreakpoints'

import useReducer from '@store/useReducer'
import * as AdminStore from '@store/admin/admin'

import api from '@api/api'
import Logger from '@utils/logger'
import sanitize from '@utils/Sanitizer'

const AdminRoleListScreen = () => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const isPhone = useBreakpoint()[1] === 'phone'

  const roles = useReducer(AdminStore.store, (s) => s.roles)

  const [status, setStatus] = React.useState<'fetched' | 'loading'>('loading')
  const [search, setSearch] = React.useState('')

  React.useEffect(() => {
    api.roles
      .all()
      .then(AdminStore.actions.setRoles)
      .catch(Logger.error)
      .finally(() => setStatus('fetched'))
  }, [])

  const items = React.useMemo(
    () => (search ? roles.filter((r) => sanitize(r.name).includes(sanitize(search))) : roles),
    [roles, search]
  )

  const AddRoleButton: HeaderButton = {
    label: i18n.t('screens.admin.roles.buttons.add'),
    onClick: () => navigation.push('/admin/roles/form/info'),
  }

  const renderCard = ({ item }: { item: RoleResponse }) => (
    <CardContainer activeOpacity={0.9} onPress={() => navigation.push(`/admin/roles/${item.id}`)}>
      <RoleCard role={item} />
    </CardContainer>
  )

  const renderSearch = () => (
    <SearchContainer>
      <TextInputComponent
        onChange={setSearch}
        searchStyle="onDark"
        placeholder={i18n.t('screens.incidentList.search')}
        left={{ name: 'search' }}
        value={search}
      />
    </SearchContainer>
  )

  return (
    <Root
      loading={status === 'loading'}
      header={isPhone ? i18n.t('screens.admin.roles.title') : undefined}
      renderSubHeader={isPhone ? renderSearch : undefined}
      fixed={isPhone}
      onLeft={navigation.goBack}
      right="add"
      onRight={AddRoleButton.onClick}>
      {!isPhone && (
        <HeaderContainer>
          <Header title={i18n.t('screens.admin.roles.title')} renderBottom={renderSearch} buttons={[AddRoleButton]} />
        </HeaderContainer>
      )}
      <FlatList
        data={items}
        renderItem={renderCard}
        contentContainerStyle={{
          flexWrap: isPhone ? undefined : 'wrap',
          flexDirection: isPhone ? 'column' : 'row',
          marginVertical: 20,
          marginHorizontal: isPhone ? 16 : 25,
          justifyContent: isPhone ? 'center' : 'flex-start',
        }}
        ListEmptyComponent={<MissingInfo>{i18n.t('screens.admin.roles.empty')}</MissingInfo>}
        showsVerticalScrollIndicator={false}
      />
    </Root>
  )
}

export default AdminRoleListScreen

const HeaderContainer = styled(View)`
  z-index: 10;
`

const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0px;`}
`

const CardContainer = styled(TouchableOpacity)<{ disabled?: boolean }>`
  margin-bottom: 10px;
  ${(props) => (props.theme.breakpoint === 'phone' ? `margin: 4px;` : `margin-right: 16px;`)};
`

const SearchContainer = styled(View)`
  margin-top: ${(props) => (props.theme.breakpoint === 'phone' ? 12 : 24)}px;
`
